import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'nyafaz-sample-products-cart';

export function CartProvider({ children, initialItems = [] }) {
  const [items, setItems] = useState(initialItems);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (initialItems.length) {
      setLoaded(true);
      return;
    }
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setItems(JSON.parse(saved));
      } catch {
        setItems([]);
      }
    }
    setLoaded(true);
  }, [initialItems.length]);

  useEffect(() => {
    if (!loaded) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, loaded]);

  const value = useMemo(
    () => ({
      items,
      count: items.reduce((sum, item) => sum + item.quantity, 0),
      addItem: (product, quantity = 1) => {
        setItems((current) => {
          const existing = current.find((item) => item.slug === product.slug);
          if (existing) {
            return current.map((item) =>
              item.slug === product.slug ? { ...item, quantity: item.quantity + quantity } : item
            );
          }
          const { slug, name, sku, variant_id, imageUrl, mrp, price, discount } = product;
          return [...current, { slug, name, sku, variant_id, imageUrl, mrp, price, discount, quantity }];
        });
      },
      setProductQuantity: (product, quantity) => {
        if (quantity < 1) return;
        setItems((current) => {
          const { slug, name, sku, variant_id, imageUrl, mrp, price, discount } = product;
          const item = { slug, name, sku, variant_id, imageUrl, mrp, price, discount, quantity };
          return current.some((currentItem) => currentItem.slug === slug)
            ? current.map((currentItem) => (currentItem.slug === slug ? item : currentItem))
            : [...current, item];
        });
      },
      removeItem: (slug) => setItems((current) => current.filter((item) => item.slug !== slug)),
      updateQuantity: (slug, quantity) => {
        if (quantity < 1) return;
        setItems((current) => current.map((item) => (item.slug === slug ? { ...item, quantity } : item)));
      },
      clear: () => setItems([]),
    }),
    [items]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used inside CartProvider');
  return context;
}
